import React, { useState, useMemo } from 'react';
import { CardSectionProps } from '../../../types/compositeCards';

// ArtifactList component - displays list of artifacts with selection capability
export const ArtifactList: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { data } = card;
  
  // State for filtering, sorting, and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'type' | 'status' | 'created_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Configurable limit
  
  // Get items and itemType - handle both cases
  const allArtifacts = ('items' in data && Array.isArray(data.items)) ? data.items : [];
  const itemType = (data as any).itemType || 'artifact';

  // Filter and sort artifacts - useMemo must be called before any early returns
  const filteredAndSortedArtifacts = useMemo(() => {
    if (!Array.isArray(allArtifacts) || allArtifacts.length === 0) {
      return [];
    }
    let filtered = allArtifacts.filter(artifact => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        artifact.name?.toLowerCase().includes(searchLower) ||
        artifact.type?.toLowerCase().includes(searchLower) ||
        artifact.description?.toLowerCase().includes(searchLower)
      );
    });

    // Sort artifacts
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle different data types
      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allArtifacts, searchTerm, sortBy, sortOrder]);

  // Paginate results
  const totalPages = Math.ceil(filteredAndSortedArtifacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const artifacts = filteredAndSortedArtifacts.slice(startIndex, startIndex + itemsPerPage);

  // Early return after all hooks have been called
  if (!('items' in data) || !Array.isArray(data.items)) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fca5a5',
        borderRadius: '8px',
        margin: '8px 0',
      }}>
        <p style={{
          margin: 0,
          color: '#dc2626',
          fontSize: '14px',
        }}>
          No artifacts available to display.
        </p>
      </div>
    );
  }

  const handleArtifactSelect = (artifact: any) => {
    console.log('🎯 [ArtifactList] Artifact selected:', artifact);
    onAction?.('select_artifact', { 
      artifactId: artifact.id,
      artifact: artifact
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
      case 'inactive':
        return { backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#d1d5db' };
      case 'draft':
        return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'archived':
        return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
      default:
        return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#bfdbfe' };
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'financial_account':
      case 'business_entity':
        return { backgroundColor: '#e9d5ff', color: '#6b21a8', borderColor: '#ddd6fe' };
      case 'process':
        return { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#bfdbfe' };
      case 'document':
        return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
      case 'improvement':
        return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'solution':
        return { backgroundColor: '#d1fae5', color: '#065f46', borderColor: '#a7f3d0' };
      case 'situation':
        return { backgroundColor: '#e0e7ff', color: '#3730a3', borderColor: '#c7d2fe' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#d1d5db' };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      margin: '8px 0',
      overflow: 'hidden'
    }}>
      {/* Header with Controls */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h4 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: '600',
            color: '#111827'
          }}>
            {itemType === 'artifact' ? 'Artifacts' : 'Items'} ({filteredAndSortedArtifacts.length})
          </h4>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as any);
                setCurrentPage(1); // Reset to first page when sorting changes
              }}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: 'white'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="type">Sort by Type</option>
              <option value="status">Sort by Status</option>
              <option value="created_at">Sort by Date</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
        
        {/* Search Input */}
        <input
          type="text"
          placeholder={`Search ${itemType}s...`}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page when search changes
          }}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        />
      </div>

      {/* Artifacts List */}
      <div style={{
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        {artifacts.length === 0 ? (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              No {itemType}s available
            </p>
          </div>
        ) : (
          <div>
            {artifacts.map((artifact, index) => (
              <div
                key={artifact.id}
                onClick={() => handleArtifactSelect(artifact)}
                style={{
                  padding: '12px 16px',
                  borderBottom: index < artifacts.length - 1 ? '1px solid #e5e7eb' : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '6px',
                      flexWrap: 'wrap'
                    }}>
                      <h5 style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#111827',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artifact.name}
                      </h5>
                      {artifact.status && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 6px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: '500',
                          border: '1px solid',
                          ...getStatusColor(artifact.status)
                        }}>
                          {artifact.status}
                        </span>
                      )}
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 6px',
                        borderRadius: '9999px',
                        fontSize: '11px',
                        fontWeight: '500',
                        border: '1px solid',
                        ...getTypeColor(artifact.type)
                      }}>
                        {artifact.type?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {artifact.description && (
                      <p style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginBottom: '6px',
                        lineHeight: '1.4',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {artifact.description}
                      </p>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '11px',
                      color: '#6b7280'
                    }}>
                      <span>Created {formatDate(artifact.created_at)}</span>
                      <span>ID: {artifact.id?.slice(0, 8)}...</span>
                    </div>
                  </div>
                  
                  <div style={{
                    marginLeft: '8px',
                    color: '#9ca3af',
                    fontSize: '16px'
                  }}>
                    →
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with Pagination */}
      {(artifacts.length > 0 || filteredAndSortedArtifacts.length > 0) && (
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f3f4f6'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            <div>
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredAndSortedArtifacts.length)} of {filteredAndSortedArtifacts.length} {itemType}s
            </div>
            
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid #d1d5db',
                    borderRadius: '3px',
                    fontSize: '11px',
                    backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    color: currentPage === 1 ? '#9ca3af' : '#374151'
                  }}
                >
                  ←
                </button>
                
                <span style={{ fontSize: '11px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '2px 6px',
                    border: '1px solid #d1d5db',
                    borderRadius: '3px',
                    fontSize: '11px',
                    backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151'
                  }}
                >
                  →
                </button>
              </div>
            )}
          </div>
          
          {artifacts.length > 0 && (
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Click on any {itemType} to view details
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// TODO: Implement ProcessList component
export const ProcessList: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      margin: '8px 0',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#f59e0b',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
          TODO
        </div>
        <h4 style={{
          margin: 0,
          color: '#92400e',
          fontSize: '14px',
          fontWeight: '500',
        }}>
          Process List
        </h4>
      </div>
      <p style={{
        margin: 0,
        color: '#92400e',
        fontSize: '12px',
        lineHeight: '1.4',
      }}>
        TODO: Implement list of processes with filtering, sorting, and search capabilities.
      </p>
    </div>
  );
};

// TODO: Implement ArtifactItemList component
export const ArtifactItemList: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      margin: '8px 0',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          backgroundColor: '#f59e0b',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
          TODO
        </div>
        <h4 style={{
          margin: 0,
          color: '#92400e',
          fontSize: '14px',
          fontWeight: '500',
        }}>
          Artifact Item List
        </h4>
      </div>
      <p style={{
        margin: 0,
        color: '#92400e',
        fontSize: '12px',
        lineHeight: '1.4',
      }}>
        TODO: Implement list of artifact items with filtering, sorting, and search capabilities.
      </p>
    </div>
  );
};
